import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Tabs,
  Button,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Space,
  Upload,
  Switch,
  Alert,
  Table,
  Tag,
  Statistic,
  Checkbox,
  Progress,
  Divider,
  App,
  InputNumber,
  Badge,
  Grid,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusCircleOutlined,
  FileTextOutlined,
  UploadOutlined,
  SyncOutlined,
  CloudDownloadOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DatabaseOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  ApiOutlined,
  MobileOutlined,
  GlobalOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { gamesAPI, categoriesAPI, tagsAPI } from '../services/api';
import {
  adminAPI,
  StorageConnector,
  GamePixImportOptions,
  GameMonetizeImportOptions,
  GameDistributionImportOptions,
  BundledGamesStatusResponse,
  BundledGamesSyncResponse,
  BundledGameItem,
} from '../services/adminAPI';
import { filterSelectOption, sortByName } from '../utils/searchUtils';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Dragger } = Upload;
const { TextArea } = Input;

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface TagItem {
  id: number;
  name: string;
  slug: string;
}

const SAMPLE_GAMES_JSON = [
  {
    name: 'Cyber Blade Runner',
    slug: 'cyber-blade-runner',
    description: 'An exciting neon cyberpunk action runner game with high-speed jumps and obstacles.',
    instructions: 'Use Arrow Keys or Space to jump and slide. Avoid spikes and lasers.',
    gameUrl: 'https://games.cdn.example.com/cyber-blade/index.html',
    iconUrl: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=200&h=200&fit=crop',
    developer: 'Neon Studio',
    categoryIds: [1],
    tagIds: [1],
    isMobile: true,
    isFeatured: true,
    status: 'APPROVED',
    width: 800,
    height: 600
  },
  {
    name: 'Mystic Gem Match 3',
    slug: 'mystic-gem-match-3',
    description: 'Match sparkling runes and magical gems in over 100 enchanting puzzle levels.',
    instructions: 'Click and drag adjacent gems to swap and match 3 or more of the same color.',
    gameUrl: 'https://games.cdn.example.com/mystic-gem/index.html',
    iconUrl: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=200&h=200&fit=crop',
    developer: 'PuzzleCraft',
    categoryIds: [2],
    tagIds: [2],
    isMobile: true,
    isFeatured: false,
    status: 'APPROVED',
    width: 720,
    height: 1280
  },
  {
    name: 'Formula Speed Racing 3D',
    slug: 'formula-speed-racing-3d',
    description: 'High octane formula racing on extreme championship tracks around the world.',
    instructions: 'W/A/S/D or Arrow keys to steer, accelerate and brake. Space for handbrake.',
    gameUrl: 'https://games.cdn.example.com/formula-speed/index.html',
    iconUrl: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=200&h=200&fit=crop',
    developer: 'Velocity Games',
    categoryIds: [3],
    tagIds: [3],
    isMobile: false,
    isFeatured: true,
    status: 'APPROVED',
    width: 960,
    height: 540
  }
];

const AddGames: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message, modal } = App.useApp();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // Active tab state (synced with url ?tab=...)
  const validTabs = ['manual', 'json', 'upload', 'bundled', 'external'];
  const initialTab = searchParams.get('tab') || searchParams.get('mode') || 'manual';
  const [activeTab, setActiveTab] = useState<string>(validTabs.includes(initialTab) ? initialTab : 'manual');

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  // Shared master data
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [storageConnectors, setStorageConnectors] = useState<StorageConnector[]>([]);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [catsRes, tagsRes, connRes] = await Promise.all([
          categoriesAPI.getAll(true).catch(() => ({ data: [] })),
          tagsAPI.getAll().catch(() => ({ data: [] })),
          adminAPI.getStorageConnectors().catch(() => ({ data: [] })),
        ]);
        setCategories(sortByName(catsRes.data || []));
        setTags(sortByName(tagsRes.data || []));
        setStorageConnectors(connRes.data || []);
      } catch (err) {
        console.error('Error loading master data:', err);
      }
    };
    fetchMasterData();
  }, []);

  // -------------------------------------------------------------
  // TAB 1: MANUAL SINGLE GAME STATE & HANDLERS
  // -------------------------------------------------------------
  const [manualForm] = Form.useForm();
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualIconUrl, setManualIconUrl] = useState('');

  const handleManualNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const currentSlug = manualForm.getFieldValue('slug');
    if (!currentSlug || currentSlug === manualForm.getFieldValue('_autoSlug')) {
      const generatedSlug = val
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      manualForm.setFieldsValue({ slug: generatedSlug, _autoSlug: generatedSlug });
    }
  };

  const handleManualSubmit = async (values: any) => {
    try {
      setManualSubmitting(true);
      const slug = (values.slug || values.name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      const payload = {
        name: values.name.trim(),
        slug,
        description: values.description?.trim() || '',
        instructions: values.instructions?.trim() || null,
        developer: values.developer?.trim() || 'Admin',
        gameUrl: values.gameUrl.trim(),
        iconUrl: values.iconUrl?.trim() || '',
        videoUrl: values.videoUrl?.trim() || null,
        categoryIds: values.categoryIds || [],
        tagIds: values.tagIds || [],
        status: values.status || 'APPROVED',
        isFeatured: values.isFeatured || false,
        isMobile: values.isMobile !== undefined ? values.isMobile : true,
        width: values.width || 800,
        height: values.height || 600,
        creationType: 'manual',
      };

      await gamesAPI.create(payload);
      message.success(`Game "${payload.name}" created successfully!`);
      modal.success({
        title: 'Game Created Successfully',
        content: `"${payload.name}" has been added with slug "${slug}".`,
        okText: 'Back to Games',
        cancelText: 'Add Another Game',
        closable: true,
        onOk: () => navigate('/games'),
        onCancel: () => {
          manualForm.resetFields();
          setManualIconUrl('');
        },
      });
    } catch (error: any) {
      console.error('Failed to create manual game:', error);
      message.error(error.response?.data?.message || 'Failed to create game. Please try again.');
    } finally {
      setManualSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // TAB 2: BATCH JSON IMPORT STATE & HANDLERS
  // -------------------------------------------------------------
  const [jsonText, setJsonText] = useState('');
  const [jsonInputMode, setJsonInputMode] = useState<'upload' | 'paste'>('upload');
  const [jsonParsedGames, setJsonParsedGames] = useState<any[]>([]);
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);
  const [jsonDefaultStatus, setJsonDefaultStatus] = useState<string>('APPROVED');
  const [jsonSkipExisting, setJsonSkipExisting] = useState<boolean>(true);
  const [jsonImporting, setJsonImporting] = useState<boolean>(false);
  const [jsonImportProgress, setJsonImportProgress] = useState<number>(0);
  const [jsonImportResult, setJsonImportResult] = useState<any | null>(null);

  const parseJsonData = (rawString: string) => {
    if (!rawString.trim()) {
      setJsonParsedGames([]);
      setJsonParseError(null);
      return;
    }
    try {
      const parsed = JSON.parse(rawString);
      const items = Array.isArray(parsed) ? parsed : (parsed.games || parsed.data || []);
      if (!Array.isArray(items)) {
        setJsonParseError('JSON structure must be an array of game objects or contain a "games" array.');
        setJsonParsedGames([]);
        return;
      }
      setJsonParsedGames(items);
      setJsonParseError(null);
    } catch (err: any) {
      setJsonParseError(`Invalid JSON syntax: ${err.message}`);
      setJsonParsedGames([]);
    }
  };

  const handleJsonFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setJsonText(content);
      parseJsonData(content);
    };
    reader.readAsText(file);
    return false;
  };

  const handleDownloadSampleJson = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(SAMPLE_GAMES_JSON, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', 'sample-games.json');
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const handleLoadSampleJson = () => {
    const formatted = JSON.stringify(SAMPLE_GAMES_JSON, null, 2);
    setJsonText(formatted);
    parseJsonData(formatted);
    message.info('Sample data loaded for preview.');
  };

  const jsonValidationStats = useMemo(() => {
    const total = jsonParsedGames.length;
    let validCount = 0;
    let invalidCount = 0;
    jsonParsedGames.forEach((g) => {
      if (g.name && (g.gameUrl || g.url)) {
        validCount++;
      } else {
        invalidCount++;
      }
    });
    return { total, validCount, invalidCount };
  }, [jsonParsedGames]);

  const handleExecuteJsonImport = async () => {
    if (!jsonParsedGames.length) {
      message.warning('No games to import. Please upload or paste valid JSON.');
      return;
    }
    if (jsonValidationStats.validCount === 0) {
      message.error('No valid games found. Ensure games contain "name" and "gameUrl".');
      return;
    }

    try {
      setJsonImporting(true);
      setJsonImportProgress(30);
      setJsonImportResult(null);

      const response = await gamesAPI.createBulk({
        games: jsonParsedGames,
        defaultStatus: jsonDefaultStatus,
        skipExisting: jsonSkipExisting,
      });

      setJsonImportProgress(100);
      const resData = response.data || {};
      setJsonImportResult(resData);
      message.success(`Batch import completed: ${resData.imported || 0} games imported!`);
    } catch (err: any) {
      console.error('Batch import failed:', err);
      message.error(err.response?.data?.message || 'Failed to process bulk import.');
    } finally {
      setJsonImporting(false);
    }
  };

  // -------------------------------------------------------------
  // TAB 3: UPLOAD HTML5 ZIP PACKAGE STATE & HANDLERS
  // -------------------------------------------------------------
  const [uploadForm] = Form.useForm();
  const [zipFileList, setZipFileList] = useState<any[]>([]);
  const [zipUploading, setZipUploading] = useState(false);
  const [zipUploadSuccess, setZipUploadSuccess] = useState(false);
  const [zipCreatedGame, setZipCreatedGame] = useState<any>(null);

  useEffect(() => {
    const activeConn = storageConnectors.find((c) => c.isActive);
    if (activeConn) {
      uploadForm.setFieldValue('storageProvider', activeConn.id);
    }
  }, [storageConnectors, uploadForm]);

  const handleZipUploadSubmit = async (values: any) => {
    if (!zipFileList.length) {
      message.error('Please select a ZIP file containing the HTML5 game.');
      return;
    }

    const file = zipFileList[0];
    const isZip = file.name.toLowerCase().endsWith('.zip');
    if (!isZip) {
      message.error('Only .zip files are supported!');
      return;
    }

    const MAX_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      message.error('File size exceeds the 100MB limit!');
      return;
    }

    const formData = new FormData();
    if (file.originFileObj) {
      formData.append('gameFile', file.originFileObj);
    } else {
      formData.append('gameFile', file);
    }

    const slug = values.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const uploadData = {
      name: values.name.trim(),
      slug,
      description: values.description || '',
      categoryIds: values.categoryIds || [],
      tagIds: values.tagIds || [],
      status: values.status || 'APPROVED',
      isFeatured: values.isFeatured || false,
      sortOrder: values.sortOrder || 0,
      storageProvider: values.storageProvider || 'active',
      creationType: 'upload',
    };

    formData.append('uploadData', JSON.stringify(uploadData));

    try {
      setZipUploading(true);
      const res = await gamesAPI.uploadGame(formData);
      message.success('Game uploaded and extracted successfully to storage!');
      setZipUploadSuccess(true);
      setZipCreatedGame(res.game || res);
      uploadForm.resetFields();
      setZipFileList([]);
    } catch (err: any) {
      console.error('ZIP Upload failed:', err);
      message.error(err.response?.data?.message || 'Failed to upload HTML5 game.');
    } finally {
      setZipUploading(false);
    }
  };

  // -------------------------------------------------------------
  // TAB 4: SYNC BUNDLED GAMES STATE & HANDLERS
  // -------------------------------------------------------------
  const [bundledLoading, setBundledLoading] = useState(false);
  const [bundledSyncing, setBundledSyncing] = useState(false);
  const [bundledStatusData, setBundledStatusData] = useState<BundledGamesStatusResponse['data'] | null>(null);
  const [bundledSeedDb, setBundledSeedDb] = useState(true);
  const [bundledUploadStorage, setBundledUploadStorage] = useState(true);
  const [bundledSyncResult, setBundledSyncResult] = useState<BundledGamesSyncResponse['data'] | null>(null);
  const [bundledFrontendUrl, setBundledFrontendUrl] = useState<string>(() => {
    return localStorage.getItem('bundled_games_frontend_url') || '';
  });

  const fetchBundledStatus = async () => {
    try {
      setBundledLoading(true);
      const res = await adminAPI.getBundledGamesStatus();
      if (res.success) {
        setBundledStatusData(res.data);
        if (!bundledFrontendUrl && res.data.detectedFrontendUrl) {
          setBundledFrontendUrl(res.data.detectedFrontendUrl);
        }
      }
    } catch (err: any) {
      console.error('Failed to load bundled games status:', err);
    } finally {
      setBundledLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'bundled') {
      fetchBundledStatus();
    }
  }, [activeTab]);

  const handleBundledSync = async () => {
    if (!bundledSeedDb && !bundledUploadStorage) {
      message.warning('Please select at least one sync action (Database or Storage).');
      return;
    }

    try {
      setBundledSyncing(true);
      setBundledSyncResult(null);

      if (bundledFrontendUrl.trim()) {
        try {
          localStorage.setItem('bundled_games_frontend_url', bundledFrontendUrl.trim());
        } catch {}
      }

      const res = await adminAPI.syncBundledGames({
        seedDb: bundledSeedDb,
        uploadStorage: bundledUploadStorage,
        frontendBaseUrl: bundledFrontendUrl.trim() || undefined,
      });

      if (res.data?.success || res.data?.database?.success) {
        message.success(res.data.message || res.message || 'Bundled games synchronized successfully!');
        setBundledSyncResult(res.data);
        await fetchBundledStatus();
      } else {
        message.error(res.data?.message || res.message || 'Synchronization failed');
      }
    } catch (err: any) {
      console.error('Bundled sync error:', err);
      message.error(err.response?.data?.message || 'Failed to sync bundled games');
    } finally {
      setBundledSyncing(false);
    }
  };

  // -------------------------------------------------------------
  // TAB 5: EXTERNAL PROVIDERS (GamePix, GameDistribution, GameMonetize, Auto-Import)
  // -------------------------------------------------------------
  const [externalProviderTab, setExternalProviderTab] = useState<'gamepix' | 'gamedistribution' | 'gamemonetize' | 'autoimport'>('gamepix');
  const [gamepixForm] = Form.useForm();
  const [gameDistributionForm] = Form.useForm();
  const [gamemonetizeForm] = Form.useForm();
  const [importingExternal, setImportingExternal] = useState(false);
  const [savedGamePixSid, setSavedGamePixSid] = useState<string>(() => localStorage.getItem('gamepix_sid') || '');

  // Auto-import schedule states
  const [autoImportConfig, setAutoImportConfig] = useState<any>(null);
  const [loadingAutoImport, setLoadingAutoImport] = useState(false);
  const [triggeringScheduled, setTriggeringScheduled] = useState(false);

  const fetchAutoImportConfig = async () => {
    try {
      setLoadingAutoImport(true);
      const res = await adminAPI.getAutoImportConfig();
      if (res.success) {
        setAutoImportConfig(res.data);
      }
    } catch (err) {
      console.error('Failed to load auto import config:', err);
    } finally {
      setLoadingAutoImport(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'external') {
      fetchAutoImportConfig();
    }
  }, [activeTab]);

  const handleGamePixImport = async (values: GamePixImportOptions) => {
    try {
      setImportingExternal(true);
      if (values.sid) {
        localStorage.setItem('gamepix_sid', values.sid);
        setSavedGamePixSid(values.sid);
      }
      const res = await adminAPI.importFromGamePix(values);
      if (res.success) {
        message.success(res.message || `Imported ${res.imported} games from GamePix!`);
      } else {
        message.error(res.message || 'GamePix import failed');
      }
    } catch (err: any) {
      console.error('GamePix import error:', err);
      message.error(err.response?.data?.message || 'Failed to import from GamePix');
    } finally {
      setImportingExternal(false);
    }
  };

  const handleGameDistributionImport = async (values: GameDistributionImportOptions) => {
    try {
      setImportingExternal(true);
      const res = await adminAPI.importFromGameDistribution(values);
      if (res.success) {
        message.success(res.message || `Imported ${res.imported} games from GameDistribution!`);
      } else {
        message.error(res.message || 'GameDistribution import failed');
      }
    } catch (err: any) {
      console.error('GameDistribution import error:', err);
      message.error(err.response?.data?.message || 'Failed to import from GameDistribution');
    } finally {
      setImportingExternal(false);
    }
  };

  const handleGameMonetizeImport = async (values: GameMonetizeImportOptions) => {
    try {
      setImportingExternal(true);
      const res = await adminAPI.importFromGameMonetize(values);
      if (res.success) {
        message.success(res.message || `Imported ${res.imported} games from GameMonetize!`);
      } else {
        message.error(res.message || 'GameMonetize import failed');
      }
    } catch (err: any) {
      console.error('GameMonetize import error:', err);
      message.error(err.response?.data?.message || 'Failed to import from GameMonetize');
    } finally {
      setImportingExternal(false);
    }
  };

  const handleToggleAutoImport = async (provider: 'gamePix' | 'gameMonetize', enabled: boolean) => {
    try {
      const config = provider === 'gamePix' ? { gamePix: { enabled } } : { gameMonetize: { enabled } };
      const res = await adminAPI.updateAutoImportConfig(config);
      if (res.success) {
        setAutoImportConfig(res.data);
        message.success(`${provider} auto-import ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to update auto-import status');
    }
  };

  const handleTriggerScheduledNow = async () => {
    try {
      setTriggeringScheduled(true);
      const res = await adminAPI.triggerScheduledImport();
      if (res.success) {
        message.success(res.message || 'Scheduled import triggered. Check Import Logs for progress.');
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to trigger scheduled import');
    } finally {
      setTriggeringScheduled(false);
    }
  };

  // -------------------------------------------------------------
  // TAB ITEMS DEFINITION
  // -------------------------------------------------------------
  const tabItems = [
    {
      key: 'manual',
      label: (
        <span>
          <PlusCircleOutlined /> Manual Game
        </span>
      ),
      children: (
        <Card title="Add Single Game Manually" bordered={false}>
          <Form
            form={manualForm}
            layout="vertical"
            onFinish={handleManualSubmit}
            initialValues={{
              status: 'APPROVED',
              isFeatured: false,
              isMobile: true,
              width: 800,
              height: 600,
            }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Game Name"
                  rules={[
                    { required: true, message: 'Please enter game name' },
                    { min: 2, message: 'Name must be at least 2 characters' },
                  ]}
                >
                  <Input placeholder="e.g. Cyber Blade Runner" onChange={handleManualNameChange} />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="slug"
                  label="URL Slug"
                  rules={[
                    { required: true, message: 'Please specify a URL slug' },
                    { pattern: /^[a-z0-9-]+$/, message: 'Slug can only contain lowercase letters, numbers, and hyphens' },
                  ]}
                  tooltip="Used in the game URL: /play/:slug"
                >
                  <Input placeholder="e.g. cyber-blade-runner" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="description"
                  label="Game Description"
                  rules={[{ required: true, message: 'Please enter description' }]}
                >
                  <TextArea rows={3} placeholder="Engaging summary of gameplay, story, or mechanics..." />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="gameUrl"
                  label="Game Play / Iframe URL"
                  rules={[
                    { required: true, message: 'Please enter game URL' },
                    { type: 'url', message: 'Please enter a valid URL' },
                  ]}
                  tooltip="The URL where the HTML5 game is hosted (iframe embed or S3 URL)"
                >
                  <Input placeholder="https://example.com/games/my-game/index.html" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="iconUrl"
                  label="Game Icon / Thumbnail URL"
                  rules={[
                    { required: true, message: 'Please enter icon URL' },
                    { type: 'url', message: 'Please enter a valid image URL' },
                  ]}
                >
                  <Input
                    placeholder="https://example.com/games/my-game/icon.png"
                    onChange={(e) => setManualIconUrl(e.target.value)}
                  />
                </Form.Item>
                {manualIconUrl && (
                  <div style={{ marginTop: -8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Icon Preview:</Text>
                    <img
                      src={manualIconUrl}
                      alt="Icon Preview"
                      style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', border: '1px solid #d9d9d9' }}
                      onError={(e) => {
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </Col>

              <Col xs={24} md={12}>
                <Form.Item name="categoryIds" label="Categories" rules={[{ required: true, message: 'Please select at least one category' }]}>
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder="Select categories"
                    optionFilterProp="label"
                    filterOption={filterSelectOption}
                    options={categories.map((c) => ({ value: c.id, label: c.name }))}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item name="tagIds" label="Tags">
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder="Select tags"
                    optionFilterProp="label"
                    filterOption={filterSelectOption}
                    options={tags.map((t) => ({ value: t.id, label: t.name }))}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item name="developer" label="Developer / Studio">
                  <Input placeholder="e.g. Epic Studio" />
                </Form.Item>
              </Col>

              <Col xs={12} md={8}>
                <Form.Item name="status" label="Status">
                  <Select>
                    <Option value="APPROVED">Approved (Live)</Option>
                    <Option value="DRAFT">Draft</Option>
                    <Option value="PENDING">Pending Review</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={12} md={8}>
                <Form.Item name="isFeatured" label="Featured" valuePropName="checked">
                  <Switch checkedChildren="Featured" unCheckedChildren="Standard" />
                </Form.Item>
              </Col>

              <Col xs={12} md={6}>
                <Form.Item name="isMobile" label="Device Target" valuePropName="checked">
                  <Switch checkedChildren="Mobile & Desktop" unCheckedChildren="Desktop Only" />
                </Form.Item>
              </Col>

              <Col xs={12} md={6}>
                <Form.Item name="videoUrl" label="Trailer Video URL (Optional)">
                  <Input placeholder="https://example.com/trailer.mp4" />
                </Form.Item>
              </Col>

              <Col xs={12} md={6}>
                <Form.Item name="width" label="Frame Width (px)">
                  <InputNumber min={320} max={3840} style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={12} md={6}>
                <Form.Item name="height" label="Frame Height (px)">
                  <InputNumber min={240} max={2160} style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item name="instructions" label="How to Play / Instructions (Optional)">
                  <TextArea rows={2} placeholder="Arrow keys or swipe to move, space to jump..." />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button onClick={() => navigate('/games')}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={manualSubmitting} icon={<PlusCircleOutlined />}>
                Create Game
              </Button>
            </div>
          </Form>
        </Card>
      ),
    },
    {
      key: 'json',
      label: (
        <span>
          <FileTextOutlined /> Batch JSON
        </span>
      ),
      children: (
        <Card title="Batch Import Games from JSON" bordered={false}>
          <Alert
            message="Batch Import Multiple Games"
            description="Upload a JSON file containing an array of games or paste raw JSON. You can preview all items, validate fields, and import them in one batch."
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
            action={
              <Space>
                <Button size="small" icon={<CodeOutlined />} onClick={handleLoadSampleJson}>
                  Load Sample
                </Button>
                <Button size="small" type="primary" ghost icon={<CloudDownloadOutlined />} onClick={handleDownloadSampleJson}>
                  Download Template
                </Button>
              </Space>
            }
          />

          <Row gutter={[24, 24]}>
            <Col xs={24} md={14}>
              <Tabs
                activeKey={jsonInputMode}
                onChange={(k: any) => setJsonInputMode(k)}
                items={[
                  {
                    key: 'upload',
                    label: 'Upload .json File',
                    children: (
                      <Dragger
                        accept=".json,application/json"
                        beforeUpload={handleJsonFileUpload}
                        showUploadList={false}
                        style={{ padding: 24, background: '#fafafa' }}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined style={{ color: '#1677ff', fontSize: 44 }} />
                        </p>
                        <p className="ant-upload-text">Click or drag your games JSON file here</p>
                        <p className="ant-upload-hint">File must contain an array of games or {`{ "games": [...] }`}</p>
                      </Dragger>
                    ),
                  },
                  {
                    key: 'paste',
                    label: 'Paste JSON Text',
                    children: (
                      <div>
                        <TextArea
                          rows={10}
                          value={jsonText}
                          placeholder='[\n  {\n    "name": "Game Title",\n    "gameUrl": "https://...",\n    "iconUrl": "https://..."\n  }\n]'
                          onChange={(e) => {
                            setJsonText(e.target.value);
                            parseJsonData(e.target.value);
                          }}
                          style={{ fontFamily: 'monospace', fontSize: 13 }}
                        />
                      </div>
                    ),
                  },
                ]}
              />

              {jsonParseError && (
                <Alert
                  message="JSON Syntax Error"
                  description={jsonParseError}
                  type="error"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </Col>

            <Col xs={24} md={10}>
              <Card size="small" title="Import Settings & Summary" style={{ background: '#fcfcfc' }}>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic title="Total Found" value={jsonValidationStats.total} prefix={<FileTextOutlined />} />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Valid"
                        value={jsonValidationStats.validCount}
                        valueStyle={{ color: '#52c41a' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Invalid"
                        value={jsonValidationStats.invalidCount}
                        valueStyle={{ color: jsonValidationStats.invalidCount > 0 ? '#ff4d4f' : '#8c8c8c' }}
                        prefix={<ExclamationCircleOutlined />}
                      />
                    </Col>
                  </Row>

                  <Divider style={{ margin: '12px 0' }} />

                  <div>
                    <Text strong>Default Status for Imported Games:</Text>
                    <Select
                      value={jsonDefaultStatus}
                      onChange={setJsonDefaultStatus}
                      style={{ width: '100%', marginTop: 6 }}
                    >
                      <Option value="APPROVED">Approved (Publish immediately)</Option>
                      <Option value="DRAFT">Draft (Review later)</Option>
                    </Select>
                  </div>

                  <Checkbox
                    checked={jsonSkipExisting}
                    onChange={(e) => setJsonSkipExisting(e.target.checked)}
                  >
                    Skip duplicate games (match by slug / URL)
                  </Checkbox>

                  <Button
                    type="primary"
                    icon={<RocketOutlined />}
                    size="large"
                    block
                    loading={jsonImporting}
                    disabled={jsonValidationStats.validCount === 0 || jsonImporting}
                    onClick={handleExecuteJsonImport}
                  >
                    Import {jsonValidationStats.validCount} Games Now
                  </Button>

                  {jsonText && (
                    <Button
                      icon={<ClearOutlined />}
                      block
                      onClick={() => {
                        setJsonText('');
                        setJsonParsedGames([]);
                        setJsonParseError(null);
                        setJsonImportResult(null);
                      }}
                    >
                      Clear Data
                    </Button>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>

          {jsonImporting && (
            <div style={{ marginTop: 24 }}>
              <Progress percent={jsonImportProgress} status="active" />
              <Text type="secondary">Processing batch games insertion and taxonomy mapping...</Text>
            </div>
          )}

          {jsonImportResult && (
            <Alert
              message="Batch Import Completed"
              description={
                <div>
                  <p>
                    <strong>Total:</strong> {jsonImportResult.total} | <strong>Imported:</strong>{' '}
                    <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{jsonImportResult.imported}</span> |{' '}
                    <strong>Skipped:</strong> {jsonImportResult.skipped || 0} | <strong>Failed:</strong>{' '}
                    <span style={{ color: '#ff4d4f' }}>{jsonImportResult.failed || 0}</span>
                  </p>
                  {jsonImportResult.errors && jsonImportResult.errors.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text strong type="danger">Errors encountered:</Text>
                      <ul style={{ maxHeight: 120, overflowY: 'auto', marginTop: 4 }}>
                        {jsonImportResult.errors.map((e: any, idx: number) => (
                          <li key={idx}>
                            Item #{e.index + 1} ({e.name}): {e.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              }
              type={jsonImportResult.failed > 0 ? 'warning' : 'success'}
              showIcon
              style={{ marginTop: 20 }}
              action={
                <Button type="primary" onClick={() => navigate('/games')}>
                  Go to Games List
                </Button>
              }
            />
          )}

          {jsonParsedGames.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <Divider orientation="left">Preview Detected Games ({jsonParsedGames.length})</Divider>
              <Table
                dataSource={jsonParsedGames}
                rowKey={(record, idx) => record.slug || record.name || String(idx)}
                size="small"
                pagination={{ pageSize: 5 }}
                columns={[
                  {
                    title: 'Icon',
                    key: 'icon',
                    width: 55,
                    render: (_: any, record: any) => {
                      const icon = record.iconUrl || record.thumb || record.image;
                      return icon ? (
                        <img
                          src={icon}
                          alt="thumb"
                          style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }}
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 20 }}>🎮</span>
                      );
                    },
                  },
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    key: 'name',
                    render: (text: string, record: any) => (
                      <div>
                        <Text strong>{text || <span style={{ color: '#ff4d4f' }}>Missing Name</span>}</Text>
                        {record.slug && (
                          <div style={{ fontSize: 11, color: '#8c8c8c' }}>slug: {record.slug}</div>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: 'Game URL',
                    key: 'gameUrl',
                    render: (_: any, record: any) => {
                      const url = record.gameUrl || record.url;
                      return url ? (
                        <Text ellipsis style={{ maxWidth: 280 }} copyable>
                          {url}
                        </Text>
                      ) : (
                        <Tag color="error">Missing URL</Tag>
                      );
                    },
                  },
                  {
                    title: 'Developer',
                    dataIndex: 'developer',
                    key: 'developer',
                    render: (d: string, record: any) => d || record.company || 'Admin',
                  },
                  {
                    title: 'Device',
                    key: 'device',
                    render: (_: any, record: any) => (
                      <Tag color={record.isMobile || record.mobile ? 'green' : 'blue'}>
                        {record.isMobile || record.mobile ? 'Mobile' : 'Desktop'}
                      </Tag>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </Card>
      ),
    },
    {
      key: 'upload',
      label: (
        <span>
          <UploadOutlined /> Upload HTML5 (ZIP)
        </span>
      ),
      children: (
        <Card title="Upload HTML5 Game ZIP to S3/Cloudflare R2" bordered={false}>
          {zipUploadSuccess && zipCreatedGame ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <CheckCircleOutlined style={{ fontSize: 56, color: '#52c41a', marginBottom: 16 }} />
              <Title level={3}>Game Uploaded Successfully!</Title>
              <Paragraph>"{zipCreatedGame.name}" has been uploaded, extracted, and linked to your storage.</Paragraph>
              <Space size="large" style={{ marginTop: 20 }}>
                <Button
                  type="primary"
                  onClick={() => {
                    setZipUploadSuccess(false);
                    setZipCreatedGame(null);
                  }}
                >
                  Upload Another Game
                </Button>
                {zipCreatedGame.previewUrl && (
                  <Button type="default" icon={<EyeOutlined />} href={zipCreatedGame.previewUrl} target="_blank">
                    Preview Game
                  </Button>
                )}
                <Button onClick={() => navigate('/games')}>Back to Games</Button>
              </Space>
            </div>
          ) : (
            <Form form={uploadForm} layout="vertical" onFinish={handleZipUploadSubmit}>
              <Row gutter={[24, 16]}>
                <Col xs={24} md={14}>
                  <Form.Item
                    name="name"
                    label="Game Name"
                    rules={[{ required: true, message: 'Please enter game name' }]}
                  >
                    <Input placeholder="e.g. Space Shooter 3D" />
                  </Form.Item>

                  <Form.Item name="description" label="Description">
                    <TextArea rows={3} placeholder="Describe the game..." />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="categoryIds" label="Categories" rules={[{ required: true, message: 'Select at least one category' }]}>
                        <Select
                          mode="multiple"
                          placeholder="Select categories"
                          optionFilterProp="label"
                          filterOption={filterSelectOption}
                          options={categories.map((c) => ({ value: c.id, label: c.name }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="tagIds" label="Tags">
                        <Select
                          mode="multiple"
                          placeholder="Select tags"
                          optionFilterProp="label"
                          filterOption={filterSelectOption}
                          options={tags.map((t) => ({ value: t.id, label: t.name }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="storageProvider"
                    label="Target Storage Provider"
                    rules={[{ required: true, message: 'Select storage provider' }]}
                    initialValue="active"
                    extra="Configured in Settings > Storage"
                  >
                    <Select placeholder="Select storage provider">
                      {storageConnectors.length > 0 ? (
                        storageConnectors.map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.name} ({c.providerType}){c.isActive ? ' - [Active]' : ''}
                          </Option>
                        ))
                      ) : (
                        <>
                          <Option value="active">Active Storage Provider (Default)</Option>
                          <Option value="cloudflare-r2">Cloudflare R2</Option>
                          <Option value="minio">MinIO S3</Option>
                          <Option value="aws-s3">AWS S3</Option>
                        </>
                      )}
                    </Select>
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="status" label="Status" initialValue="APPROVED">
                        <Select>
                          <Option value="APPROVED">Approved (Publish)</Option>
                          <Option value="DRAFT">Draft</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="isFeatured" label="Featured" valuePropName="checked" initialValue={false}>
                        <Switch checkedChildren="Featured" unCheckedChildren="Regular" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={10}>
                  <Form.Item
                    label="HTML5 Package (.ZIP)"
                    required
                    rules={[{ required: true, message: 'Please select a ZIP file' }]}
                  >
                    <Dragger
                      accept=".zip"
                      fileList={zipFileList}
                      maxCount={1}
                      beforeUpload={(file) => {
                        const isZip = file.name.toLowerCase().endsWith('.zip');
                        if (!isZip) {
                          message.error('You can only upload ZIP files!');
                          return Upload.LIST_IGNORE;
                        }
                        const isLt100M = file.size / 1024 / 1024 < 100;
                        if (!isLt100M) {
                          message.error('File must be smaller than 100MB!');
                          return Upload.LIST_IGNORE;
                        }
                        setZipFileList([file]);
                        return false;
                      }}
                      onRemove={() => setZipFileList([])}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ fontSize: 44, color: '#1677ff' }} />
                      </p>
                      <p className="ant-upload-text">Click or drag ZIP file here</p>
                      <p className="ant-upload-hint">HTML5 game ZIP package (max 100MB)</p>
                    </Dragger>

                    <Alert
                      message="ZIP Package Requirements"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                          <li>Must contain <code>index.html</code> at the root</li>
                          <li>All scripts, images, audio included in the zip</li>
                          <li>Automatically extracted to your S3 / Cloudflare R2 bucket</li>
                        </ul>
                      }
                      type="info"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <Button onClick={() => navigate('/games')}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={zipUploading}
                  disabled={zipFileList.length === 0}
                  icon={<UploadOutlined />}
                >
                  {zipUploading ? 'Uploading & Extracting...' : 'Upload Game to Storage'}
                </Button>
              </div>
            </Form>
          )}
        </Card>
      ),
    },
    {
      key: 'bundled',
      label: (
        <span>
          <MobileOutlined /> Sync Bundled (11)
        </span>
      ),
      children: (
        <Card title="Synchronize Mobile Bundled Games (11 Default Games)" bordered={false}>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            These 11 classic HTML5 games are built-in for instant offline mobile gameplay. Synchronize their metadata to
            PostgreSQL and upload their asset packages to Cloudflare R2 / S3 for web browsers and CDN players.
          </Paragraph>

          {bundledStatusData && (
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ textAlign: 'center', background: '#f6ffed', borderColor: '#b7eb8f' }}>
                  <Statistic
                    title="Total Bundled Games"
                    value={bundledStatusData.totalBundled}
                    prefix={<MobileOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ textAlign: 'center', background: '#e6f4ff', borderColor: '#91caff' }}>
                  <Statistic
                    title="Synced in Database"
                    value={bundledStatusData.syncedInDb}
                    suffix={`/ ${bundledStatusData.totalBundled}`}
                    prefix={<DatabaseOutlined />}
                    valueStyle={{ color: '#1677ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ textAlign: 'center', background: '#f9f0ff', borderColor: '#d3adf7' }}>
                  <Statistic
                    title="Local Bundle Files"
                    value={bundledStatusData.gamesDirectoryExists ? 'Found' : 'Missing'}
                    prefix={<InboxOutlined />}
                    valueStyle={{ color: bundledStatusData.gamesDirectoryExists ? '#722ed1' : '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          <Card
            size="small"
            title={
              <Space>
                <GlobalOutlined style={{ color: '#1677ff' }} />
                <span>Frontend Web Domain & CDN URL Configuration</span>
              </Space>
            }
            style={{ marginBottom: 16, backgroundColor: '#f0f7ff', borderColor: '#adc6ff' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Enter your frontend domain or CDN URL (e.g. <code>https://iwin.me</code>) to generate full absolute URLs for icons and game packages.
              </Text>
              <Input
                prefix={<GlobalOutlined style={{ color: '#8c8c8c' }} />}
                placeholder="e.g. https://iwin.me"
                value={bundledFrontendUrl}
                onChange={(e) => setBundledFrontendUrl(e.target.value)}
                disabled={bundledSyncing}
                addonAfter={
                  <Button type="link" size="small" onClick={() => setBundledFrontendUrl('https://iwin.me')}>
                    Use https://iwin.me
                  </Button>
                }
              />
            </Space>
          </Card>

          <Card size="small" title="Sync Actions" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Checkbox
                checked={bundledSeedDb}
                onChange={(e) => setBundledSeedDb(e.target.checked)}
                disabled={bundledSyncing}
              >
                <Space>
                  <DatabaseOutlined />
                  <Text strong>Upsert metadata in Database</Text>
                  <Text type="secondary">(Title, slug, categories, orientation, CDN URLs)</Text>
                </Space>
              </Checkbox>
              <Checkbox
                checked={bundledUploadStorage}
                onChange={(e) => setBundledUploadStorage(e.target.checked)}
                disabled={bundledSyncing}
              >
                <Space>
                  <UploadOutlined />
                  <Text strong>Upload packages to Active Storage (S3 / Cloudflare R2)</Text>
                  <Text type="secondary">(HTML5 files, assets, audio, icons for Web players)</Text>
                </Space>
              </Checkbox>
            </Space>
          </Card>

          {bundledSyncResult && (
            <Alert
              message={bundledSyncResult.success ? 'Synchronization Completed' : 'Synchronization Notice'}
              description={
                <div>
                  {bundledSyncResult.database && (
                    <p style={{ margin: '4px 0' }}>
                      <DatabaseOutlined /> <strong>Database:</strong> {bundledSyncResult.database.inserted} new games
                      inserted, {bundledSyncResult.database.updated} existing games updated. Total:{' '}
                      {bundledSyncResult.database.total} games.
                    </p>
                  )}
                  {bundledSyncResult.storage && (
                    <p style={{ margin: '4px 0' }}>
                      <UploadOutlined /> <strong>Storage:</strong> {bundledSyncResult.storage.message || `Uploaded ${bundledSyncResult.storage.totalFiles || 0} files.`}
                    </p>
                  )}
                </div>
              }
              type={bundledSyncResult.success ? 'success' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong>Bundled Games Table</Text>
            <Space>
              <Button icon={<SyncOutlined />} onClick={fetchBundledStatus} loading={bundledLoading} disabled={bundledSyncing}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<RocketOutlined />}
                onClick={handleBundledSync}
                loading={bundledSyncing}
                disabled={bundledLoading || (!bundledSeedDb && !bundledUploadStorage)}
              >
                {bundledSyncing ? 'Synchronizing...' : 'Start Synchronization'}
              </Button>
            </Space>
          </div>

          <Table
            columns={[
              {
                title: 'Icon',
                key: 'icon',
                width: 55,
                render: (_: any, record: BundledGameItem) => {
                  const previewUrl =
                    record.iconUrl ||
                    (bundledFrontendUrl ? `${bundledFrontendUrl.replace(/\/+$/, '')}/games/${record.slug}/icon.png` : null);
                  return previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={record.name}
                      style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }}
                      onError={(e) => {
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <span>🎮</span>
                  );
                },
              },
              {
                title: 'Game',
                key: 'name',
                render: (_: any, record: BundledGameItem) => (
                  <div>
                    <Text strong>{record.name}</Text>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>slug: {record.slug}</div>
                  </div>
                ),
              },
              {
                title: 'Category',
                dataIndex: 'categorySlug',
                key: 'categorySlug',
                render: (cat: string) => <Tag color="blue">{cat}</Tag>,
              },
              {
                title: 'Local Package',
                key: 'local',
                render: (_: any, record: BundledGameItem) =>
                  record.localFolderExists ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      Ready
                    </Tag>
                  ) : (
                    <Tag color="error" icon={<ExclamationCircleOutlined />}>
                      Missing
                    </Tag>
                  ),
              },
              {
                title: 'Database Status',
                key: 'inDatabase',
                render: (_: any, record: BundledGameItem) =>
                  record.inDatabase ? (
                    <Tag color="cyan" icon={<CheckCircleOutlined />}>
                      {record.status || 'APPROVED'}
                    </Tag>
                  ) : (
                    <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                      Not Seeded
                    </Tag>
                  ),
              },
            ]}
            dataSource={bundledStatusData?.games || []}
            rowKey="slug"
            size="small"
            loading={bundledLoading}
            pagination={false}
          />
        </Card>
      ),
    },
    {
      key: 'external',
      label: (
        <span>
          <ApiOutlined /> External Providers
        </span>
      ),
      children: (
        <Card title="Import Games from External Catalogues" bordered={false}>
          <Tabs
            activeKey={externalProviderTab}
            onChange={(k: any) => setExternalProviderTab(k)}
            items={[
              {
                key: 'gamepix',
                label: 'GamePix',
                children: (
                  <div>
                    <Alert
                      message="GamePix API Feed"
                      description="Import curated HTML5 games from GamePix with customizable quality thresholds, categories, and orientation."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form
                      form={gamepixForm}
                      layout="vertical"
                      onFinish={handleGamePixImport}
                      initialValues={{
                        sid: savedGamePixSid || '',
                        maxPages: 1,
                        gamesPerPage: 24,
                        filterByQuality: 0.5,
                        orientation: 'all',
                        orderBy: 'pubdate',
                      }}
                    >
                      <Form.Item
                        name="sid"
                        label="GamePix SID (Session ID)"
                        rules={[{ required: true, message: 'Please enter your GamePix SID' }]}
                        tooltip="Your GamePix account SID. Saved automatically for future imports."
                        extra={savedGamePixSid ? 'Using saved SID from previous import' : 'Your SID will be remembered'}
                      >
                        <Input.Password placeholder="Enter GamePix SID" visibilityToggle />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="maxPages" label="Max Pages to Fetch" rules={[{ required: true }]}>
                            <InputNumber min={1} max={20} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="gamesPerPage" label="Games Per Page">
                            <Select>
                              <Option value={12}>12 games</Option>
                              <Option value={24}>24 games</Option>
                              <Option value={48}>48 games</Option>
                              <Option value={96}>96 games</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="filterByQuality" label="Quality Filter">
                            <Select>
                              <Option value={0}>All Games (0)</Option>
                              <Option value={0.3}>Good Quality (0.3+)</Option>
                              <Option value={0.5}>High Quality (0.5+)</Option>
                              <Option value={0.8}>Very High Quality (0.8+)</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="orientation" label="Orientation">
                            <Select>
                              <Option value="all">All Orientations</Option>
                              <Option value="landscape">Landscape</Option>
                              <Option value="portrait">Portrait</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={importingExternal}
                        icon={<ThunderboltOutlined />}
                      >
                        {importingExternal ? 'Importing...' : 'Start GamePix Import'}
                      </Button>
                    </Form>
                  </div>
                ),
              },
              {
                key: 'gamedistribution',
                label: 'GameDistribution',
                children: (
                  <div>
                    <Alert
                      message="GameDistribution API Feed"
                      description="Import games directly from GameDistribution's extensive catalogue of popular web and mobile HTML5 games."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form
                      form={gameDistributionForm}
                      layout="vertical"
                      onFinish={handleGameDistributionImport}
                      initialValues={{
                        collection: 'all',
                        maxGames: 50,
                        maxPages: 2,
                        mobile: true,
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="collection" label="Collection">
                            <Select>
                              <Option value="all">All Games</Option>
                              <Option value="html5">HTML5 Only</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="mobile" label="Target Platform" valuePropName="checked">
                            <Switch checkedChildren="Mobile Compatible" unCheckedChildren="All Platforms" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="maxGames" label="Max Games to Import">
                            <InputNumber min={5} max={500} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="maxPages" label="Max Pages to Scan">
                            <InputNumber min={1} max={20} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={importingExternal}
                        icon={<CloudDownloadOutlined />}
                      >
                        {importingExternal ? 'Importing...' : 'Start GameDistribution Import'}
                      </Button>
                    </Form>
                  </div>
                ),
              },
              {
                key: 'gamemonetize',
                label: 'GameMonetize',
                children: (
                  <div>
                    <Alert
                      message="GameMonetize API Feed"
                      description="Import games directly from GameMonetize. Filter by name, category, publisher company, or platform."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form
                      form={gamemonetizeForm}
                      layout="vertical"
                      onFinish={handleGameMonetizeImport}
                      initialValues={{
                        maxGames: 50,
                        maxPages: 2,
                        links: 0,
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="name" label="Game Name (Optional)">
                            <Input placeholder="Leave empty for all" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="category" label="Category (Optional)">
                            <Input placeholder="e.g. Action, Puzzle, Arcade" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="maxGames" label="Max Games to Import">
                            <InputNumber min={5} max={500} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="maxPages" label="Max Pages to Fetch">
                            <InputNumber min={1} max={20} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={importingExternal}
                        icon={<CloudDownloadOutlined />}
                      >
                        {importingExternal ? 'Importing...' : 'Start GameMonetize Import'}
                      </Button>
                    </Form>
                  </div>
                ),
              },
              {
                key: 'autoimport',
                label: 'Auto-Import Schedule',
                children: (
                  <div>
                    <Alert
                      message="Daily Automated Background Import"
                      description="Games can be automatically imported every day at 2:00 AM UTC. You can toggle automated imports and trigger manual runs anytime."
                      type="info"
                      showIcon
                      style={{ marginBottom: 20 }}
                      action={
                        <Button
                          type="primary"
                          icon={<ThunderboltOutlined />}
                          onClick={handleTriggerScheduledNow}
                          loading={triggeringScheduled}
                        >
                          Trigger Schedule Now
                        </Button>
                      }
                    />

                    <Row gutter={[20, 20]}>
                      <Col xs={24} sm={12}>
                        <Card size="small" title="GamePix Auto-Import" style={{ background: '#fafafa' }}>
                          <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>Status:</span>
                              <Switch
                                checked={autoImportConfig?.gamePix?.enabled ?? false}
                                onChange={(chk) => handleToggleAutoImport('gamePix', chk)}
                                checkedChildren={<CheckCircleOutlined />}
                                unCheckedChildren={<CloseCircleOutlined />}
                              />
                            </div>
                            <div>Schedule: {autoImportConfig?.gamePix?.schedule || 'Daily at 2:00 AM UTC'}</div>
                            <div>Max Pages: {autoImportConfig?.gamePix?.maxPages || 1}</div>
                            <div>Quality Threshold: {autoImportConfig?.gamePix?.minQuality || 0.85}</div>
                          </Space>
                        </Card>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Card size="small" title="GameMonetize Auto-Import" style={{ background: '#fafafa' }}>
                          <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>Status:</span>
                              <Switch
                                checked={autoImportConfig?.gameMonetize?.enabled ?? false}
                                onChange={(chk) => handleToggleAutoImport('gameMonetize', chk)}
                                checkedChildren={<CheckCircleOutlined />}
                                unCheckedChildren={<CloseCircleOutlined />}
                              />
                            </div>
                            <div>Schedule: {autoImportConfig?.gameMonetize?.schedule || 'Daily at 2:00 AM UTC'}</div>
                            <div>Max Games: {autoImportConfig?.gameMonetize?.maxGames || 50}</div>
                            <div>Max Pages: {autoImportConfig?.gameMonetize?.maxPages || 2}</div>
                          </Space>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                ),
              },
            ]}
          />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Top Header Bar */}
      <div
        style={{
          marginBottom: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/games')}>
            Back to Games
          </Button>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Add Games
            </Title>
            <Text type="secondary">
              Centralized hub to add games manually, in bulk via JSON, upload HTML5 ZIP, sync bundled games, or import from external distributors.
            </Text>
          </div>
        </Space>

        <Space>
          <Button icon={<FileTextOutlined />} onClick={() => navigate('/import-logs')}>
            View Import Logs
          </Button>
        </Space>
      </div>

      {/* Main Tabs Container */}
      <Card style={{ borderRadius: 8 }}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          type="line"
          size="large"
          items={tabItems}
        />
      </Card>
    </div>
  );
};

export default AddGames;
